/*
 * Copyright © 2025 AionTrack. All rights reserved.
 */

import { useMemo } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useProjectMembership } from '@/hooks/useProjectMembers'
import type { ProjectRole } from '@/types/project-teams'

interface PermissionFlags {
  // Team management
  canManageMembers: boolean
  canInviteMembers: boolean
  canRemoveMembers: boolean
  canChangeRoles: boolean

  // Project management
  canEditProject: boolean
  canDeleteProject: boolean

  // Content creation
  canImportFiles: boolean
  canCreateClips: boolean
  canEditClips: boolean
  canDeleteClips: boolean
  canCreateVfxShots: boolean
  canEditVfxShots: boolean
  canDeleteVfxShots: boolean
  canCreateCommits: boolean
  canGenerateComparisons: boolean
  canCreateTranscodes: boolean

  // Assignment (editors can assign to each other)
  canAssignWork: boolean

  // Relationships/Linking
  canLinkClips: boolean
  canLinkVfxShots: boolean

  // Tags
  canCreateTags: boolean
  canEditTags: boolean
  canDeleteTags: boolean

  // Comments
  canComment: boolean

  // View access
  canViewDashboard: boolean
  canViewAllClips: boolean
  canViewCommits: boolean
  canViewVfxShots: boolean
  canViewTranscodes: boolean
  canViewTeam: boolean
  canViewActivityFeed: boolean

  // User info
  role: ProjectRole | null
  isOwner: boolean
  isAdmin: boolean
  isEditor: boolean
  isViewer: boolean
  isContributor: boolean
  loading: boolean
}

/**
 * Hook for checking user permissions in a project
 *
 * @param projectId - UUID of the project
 * @param ownerId - Optional UUID of the project owner
 * @returns Permission flags and role information
 *
 * @example
 * ```tsx
 * const permissions = usePermissions(projectId, project.owner_user_id)
 *
 * return (
 *   <div>
 *     {permissions.canCreateClips && (
 *       <Button onClick={handleCreate}>Create Clip</Button>
 *     )}
 *   </div>
 * )
 * ```
 */
export function usePermissions(projectId: string | undefined, ownerId?: string): PermissionFlags {
  const { user } = useAuth()
  const { data: membership, isLoading } = useProjectMembership(projectId)

  return useMemo(() => {
    if (!user || !projectId) {
      // Not authenticated - no permissions
      return {
        canManageMembers: false,
        canInviteMembers: false,
        canRemoveMembers: false,
        canChangeRoles: false,
        canEditProject: false,
        canDeleteProject: false,
        canImportFiles: false,
        canCreateClips: false,
        canEditClips: false,
        canDeleteClips: false,
        canCreateVfxShots: false,
        canEditVfxShots: false,
        canDeleteVfxShots: false,
        canCreateCommits: false,
        canGenerateComparisons: false,
        canCreateTranscodes: false,
        canAssignWork: false,
        canLinkClips: false,
        canLinkVfxShots: false,
        canCreateTags: false,
        canEditTags: false,
        canDeleteTags: false,
        canComment: false,
        canViewDashboard: false,
        canViewAllClips: false,
        canViewCommits: false,
        canViewVfxShots: false,
        canViewTranscodes: false,
        canViewTeam: false,
        canViewActivityFeed: false,
        role: null,
        isOwner: false,
        isAdmin: false,
        isEditor: false,
        isViewer: false,
        isContributor: false,
        loading: isLoading,
      }
    }

    const isOwner = user.id === ownerId
    const role = membership?.role || null

    // Role flags
    const isAdmin = isOwner || role === 'admin'
    const isEditor = role === 'editor'
    const isViewer = role === 'viewer'
    const isContributor = role === 'contributor'

    // Permission groups
    const canEdit = isAdmin || isEditor
    const canView = true // All team members can view

    return {
      // Team management - Admin and Owner only
      canManageMembers: isAdmin,
      canInviteMembers: isAdmin,
      canRemoveMembers: isAdmin,
      canChangeRoles: isAdmin,

      // Project management
      canEditProject: canEdit, // Admin and Editor
      canDeleteProject: isOwner, // Only owner (not even admin)

      // Content operations - Admin and Editor (NOT contributor)
      canImportFiles: canEdit,
      canCreateClips: canEdit,
      canEditClips: canEdit || isContributor, // Contributors can edit assigned clips
      canDeleteClips: canEdit, // Contributors CANNOT delete
      canCreateVfxShots: canEdit,
      canEditVfxShots: canEdit || isContributor, // Contributors can edit assigned VFX
      canDeleteVfxShots: canEdit, // Contributors CANNOT delete
      canCreateCommits: canEdit,
      canGenerateComparisons: canEdit, // Only Admin and Editor can generate comparisons
      canCreateTranscodes: canEdit,

      // Assignment - Editor and above (editors can assign to each other!)
      canAssignWork: canEdit,

      // Relationships/Linking - Admin and Editor only (contributors can't see other clips to link)
      canLinkClips: canEdit,
      canLinkVfxShots: canEdit,

      // Tags - Admin and Editor
      canCreateTags: canEdit,
      canEditTags: canEdit,
      canDeleteTags: canEdit,

      // Comments - All roles can comment
      canComment: canView,

      // View access
      canViewDashboard: !isContributor, // Contributors cannot see dashboard
      canViewAllClips: !isContributor, // Contributors only see assigned clips
      canViewCommits: canView, // But only commits affecting their clips (filtered in backend)
      canViewVfxShots: canView,
      canViewTranscodes: canView,
      canViewTeam: !isContributor, // Contributors cannot see team page
      canViewActivityFeed: !isContributor, // Contributors only see activity on assigned items

      // Role info
      role,
      isOwner,
      isAdmin,
      isEditor,
      isViewer,
      isContributor,
      loading: isLoading,
    }
  }, [user, projectId, ownerId, membership, isLoading])
}
